import {
  listAllTrainingEvalScenarios,
  listTrainingEvalRuns,
  type TrainingEvalRunFilters,
  type TrainingEvalRunSort,
  type TrainingEvalRunSortField,
  type TrainingEvalScenarioView,
} from "@/api/trainingEval";
import {
  DataTableSource,
  type DataTablePageOptions,
  type DataTablePageReq,
  type DataTablePageResp,
  type DataTableSortState,
} from "@carbon6/c6-frontend-design";
import { createLogger } from "@/logging";
import { sortSignature } from "@/components/table/sortSignature";
import type { SelectedPolicyRef, TrainingEvalRunScope } from "@/modules/training/public";
import { EvalRunTableRow, type EvalRunRowClickHandler } from "./EvalRunTableRow";
import { scenarioIndexByName } from "./evalRunScenarioModel";
import { isLiveEvalStatus } from "./evalRunFormat";

const logger = createLogger("page/evaluationResults");

// 첫 열(policyLabel)은 여기 없다. BE 가 policy_name 정렬을 지원하지 않아 이 열은 정렬할 수 없다.
const SORT_FIELD_BY_COLUMN: Readonly<Record<string, TrainingEvalRunSortField>> = {
  checkpointLabel: "epoch",
  branchHashLabel: "branch",
  datasetLabel: "dataset_name",
  taskLabel: "task",
  dockerTagLabel: "docker_tag",
  startedLabel: "started_at",
  successRateLabel: "success_rate",
  status: "status",
};

function toSortParam(sort: DataTableSortState | null): TrainingEvalRunSort | undefined {
  if (sort === null) {
    return undefined;
  }
  const field = SORT_FIELD_BY_COLUMN[sort.columnKey];
  return field === undefined ? undefined : `${field}_${sort.direction}`;
}

type EvalRunTableSourceOptions = {
  scope: EvalRunTableScope;
  pageSize: number;
  initialFilters?: TrainingEvalRunFilters;
  rowClickHandler: EvalRunRowClickHandler;
  resolveErrorMessage: (error: unknown) => string;
  // 첫 목록 요청 전에 선택 정책을 서버 필터에 포함할 때 사용한다.
  initialPolicyFilter?: readonly SelectedPolicyRef[] | null;
};

// Team 그룹을 확인하는 동안에는 null 스코프로 API 호출을 보류한다.
export type EvalRunTableScope = TrainingEvalRunScope | { kind: "team"; groupUuid: null };

// 비교 API가 한 번에 받는 eval-run 수와 같다.
export const MAX_EVAL_RUN_SELECTION = 15;

// 실행 중 행은 빠르게, 종료 행만 있는 페이지는 느리게 갱신한다.
export const LIVE_POLL_MS = 10_000;
export const IDLE_POLL_MS = 60_000;

// 평가 결과는 필터·정렬·페이지네이션·실행 중 성공률을 목록 응답 한 번으로 받는다.
export class EvalRunTableSource extends DataTableSource<EvalRunTableRow> {
  private readonly rowClickHandler: EvalRunRowClickHandler;
  private scope: EvalRunTableScope;
  private filters: TrainingEvalRunFilters = {};
  private policyIds: readonly string[];
  private groupResolving = true;
  private heldPage: (() => void) | null = null;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private pollInFlight = false;
  // 진행 중인 polling 요청을 취소한다. polling 은 c6 의 page 요청 경로 밖에서 onPage 를 직접 부른다.
  private pollController: AbortController | null = null;
  private polling = false;
  private active = true;
  private disposedSinceLoad = false;
  private scenarioIndex: Promise<ReadonlyMap<string, TrainingEvalScenarioView>> | null = null;

  constructor({
    scope,
    pageSize,
    initialFilters = {},
    rowClickHandler,
    resolveErrorMessage,
    initialPolicyFilter = null,
  }: EvalRunTableSourceOptions) {
    super({ initialPageSize: pageSize, resolveErrorMessage, prefetchMode: "paging" });
    this.scope = scope;
    this.filters = initialFilters;
    this.rowClickHandler = rowClickHandler;
    this.policyIds = normalizePolicyIds(initialPolicyFilter);
  }

  override initialize(): Promise<void> {
    this.polling = true;
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", this.handleVisibilityChange);
    }
    this.scheduleNextPoll();
    const { loadState } = this.getSnapshot();
    if (!this.disposedSinceLoad && loadState !== "idle" && loadState !== "failed") {
      return Promise.resolve();
    }
    this.disposedSinceLoad = false;
    return super.initialize();
  }

  setActive(active: boolean): void {
    if (this.active === active) {
      return;
    }
    this.active = active;
    this.setPollingEnabled(active);
  }

  private setPollingEnabled(enabled: boolean): void {
    if (!enabled) {
      this.clearPollTimer();
      return;
    }
    this.scheduleNextPoll();
    if (this.getSnapshot().loadState !== "loading") {
      void this.pollOnce();
    }
  }

  override dispose(): void {
    this.polling = false;
    this.disposedSinceLoad = true;
    this.clearPollTimer();
    this.pollController?.abort();
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    }
    super.dispose();
  }

  private readonly handleVisibilityChange = (): void => {
    this.setPollingEnabled(document.visibilityState !== "hidden");
  };

  private clearPollTimer(): void {
    if (this.pollTimer !== null) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private get canPoll(): boolean {
    if (!this.polling || !this.active) {
      return false;
    }
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      return false;
    }
    return isTrainingEvalRunScope(this.scope);
  }

  private scheduleNextPoll(): void {
    this.clearPollTimer();
    if (!this.canPoll) {
      return;
    }
    const hasLiveRow = this.getSnapshot().rows.some((row) => isLiveEvalStatus(row.status));
    this.pollTimer = setTimeout(
      () => void this.pollOnce(),
      hasLiveRow ? LIVE_POLL_MS : IDLE_POLL_MS,
    );
  }

  // 표시 페이지가 바뀔 때 현재 행 상태에 맞춰 다음 폴링 시각을 다시 잡는다.
  protected override replaceLoadedPage(response: DataTablePageResp<EvalRunTableRow>): void {
    super.replaceLoadedPage(response);
    this.scheduleNextPoll();
  }

  // 현재 페이지를 로딩 표시 없이 갱신한다. 로딩 상태와 인접 페이지 캐시를 바꾸지 않는다.
  private async pollOnce(): Promise<void> {
    if (this.pollInFlight || !this.canPoll) {
      return;
    }
    const { page, size } = this.getSnapshot();
    const signatureAtStart = this.cacheSignature();
    this.pollInFlight = true;
    const controller = new AbortController();
    this.pollController = controller;
    try {
      const response = await this.onPage({ page, size }, { signal: controller.signal });
      // 요청한 뒤에 조건·page·행 수가 바뀌었으면 이 응답은 지금 화면의 page 가 아니다.
      const current = this.getSnapshot();
      if (
        this.polling &&
        this.cacheSignature() === signatureAtStart &&
        current.page === page &&
        current.size === size
      ) {
        this.replaceLoadedPage(response);
      }
    } catch {
      // 마지막 정상 페이지를 유지하고 다음 주기에 다시 시도한다.
    } finally {
      this.pollInFlight = false;
      this.pollController = null;
      this.scheduleNextPoll();
    }
  }

  setGroupResolving(resolving: boolean): void {
    if (this.groupResolving === resolving) {
      return;
    }
    this.groupResolving = resolving;
    if (!resolving) {
      this.settleHeldPage();
    }
  }

  private settleHeldPage(): void {
    const settle = this.heldPage;
    this.heldPage = null;
    settle?.();
  }

  setScope(scope: EvalRunTableScope): Promise<void> {
    if (scopeSignature(scope) === scopeSignature(this.scope)) {
      return Promise.resolve();
    }
    this.scope = scope;
    this.settleHeldPage();
    this.clearSelection();
    return this.goToPage(1);
  }

  protected override canSelectMore(): boolean {
    return this.selectedRowCount < MAX_EVAL_RUN_SELECTION;
  }

  setFilters(filters: TrainingEvalRunFilters): Promise<void> {
    if (JSON.stringify(filters) === JSON.stringify(this.filters)) {
      return Promise.resolve();
    }
    this.filters = filters;
    return this.goToPage(1);
  }

  setPolicyFilter(policies: readonly SelectedPolicyRef[] | null): Promise<void> {
    const next = normalizePolicyIds(policies);
    if (policyIdSignature(next) === policyIdSignature(this.policyIds)) {
      return Promise.resolve();
    }
    this.policyIds = next;
    return this.goToPage(1);
  }

  override setPageSize(size: number): Promise<void> {
    if (size === this.getSnapshot().size) {
      return Promise.resolve();
    }
    return super.setPageSize(size);
  }

  override get sortsOnServer(): boolean {
    return true;
  }

  get sortedColumnKey(): string | null {
    return this.sortState?.columnKey ?? null;
  }

  override setSort(sort: DataTableSortState | null): void {
    if (sortSignature(sort) === sortSignature(this.sortState)) {
      super.setSort(sort);
      return;
    }
    super.setSort(sort);
    void this.goToPage(1);
  }

  protected override cacheSignature(): string {
    return `${scopeSignature(this.scope)}|${JSON.stringify(this.filters)}|${policyIdSignature(this.policyIds)}|${toSortParam(this.sortState) ?? ""}`;
  }

  /**
   * 실행 조건 조인에 쓰는 scenario 색인이다. 소스당 한 번만 만든다.
   *
   * `onPage` 는 폴링(10s/60s)과 이웃 페이지 prefetch 때도 실행된다. 여기서 카탈로그를 매번
   * 조회하면 실패했을 때 문제가 된다. api 계층 캐시는 성공만 저장하므로 실패하면 매 tick 마다 새
   * 요청과 새 에러 로그가 생긴다. 결과를 promise 로 보관해서 요청과 로그를 한 번으로 줄인다.
   * 카탈로그는 세션 동안 바뀌지 않으므로 다시 받을 필요도 없다.
   *
   * 실패하면 빈 색인으로 계속한다. 이 세 열은 보조 정보다. scenario 를 찾지 못한 행을 빈 칸으로
   * 표시하는 것은 정상 상태다. 결과 표 전체를 실패로 처리하지 않는다.
   */
  private loadScenarioIndex(): Promise<ReadonlyMap<string, TrainingEvalScenarioView>> {
    this.scenarioIndex ??= listAllTrainingEvalScenarios()
      .then(scenarioIndexByName)
      .catch((error: unknown) => {
        logger.error("Failed to load the eval scenario catalog for run parameters.", error);
        return new Map<string, TrainingEvalScenarioView>();
      });
    return this.scenarioIndex;
  }

  protected override async onPage(
    req: DataTablePageReq,
    { signal }: DataTablePageOptions,
  ): Promise<DataTablePageResp<EvalRunTableRow>> {
    const scope = this.scope;
    if (!isTrainingEvalRunScope(scope)) {
      const empty = { rows: [], total: 0, page: req.page, size: req.size };
      if (!this.groupResolving) {
        return empty;
      }
      this.settleHeldPage();
      return new Promise<DataTablePageResp<EvalRunTableRow>>((resolve) => {
        this.heldPage = () => resolve(empty);
      });
    }
    // SEED·ENVS·EPISODES 는 목록 응답에 없고 scenario 가 가진다. 색인은 목록과 병렬로 받는다.
    // 순서대로 요청하면 첫 화면이 왕복 2회가 된다(rules.md §7).
    const [page, scenarioByName] = await Promise.all([
      listTrainingEvalRuns(
        scope,
        { page: req.page, size: req.size },
        this.filters,
        toSortParam(this.sortState),
        this.policyIds,
        { signal },
      ),
      this.loadScenarioIndex(),
    ]);
    const nowMs = Date.now();
    return {
      rows: page.items.map(
        (run) =>
          new EvalRunTableRow(
            run,
            this.rowClickHandler,
            nowMs,
            run.scenario === null ? null : (scenarioByName.get(run.scenario) ?? null),
          ),
      ),
      total: page.total,
      page: page.page,
      size: page.size,
    };
  }
}

function normalizePolicyIds(policies: readonly SelectedPolicyRef[] | null): readonly string[] {
  return [...new Set((policies ?? []).map((policy) => policy.policyId))].sort();
}

function policyIdSignature(policyIds: readonly string[]): string {
  return policyIds.join(",");
}

function scopeSignature(scope: EvalRunTableScope): string {
  return scope.kind === "me" ? "me" : `team:${scope.groupUuid ?? ""}`;
}

function isTrainingEvalRunScope(scope: EvalRunTableScope): scope is TrainingEvalRunScope {
  return scope.kind === "me" || scope.groupUuid !== null;
}
